// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useEffect, useState } from 'react';
import { getStoredSession, onSessionChange } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import './register-scds';

// Real CRA sign-in landing page, not a placeholder -- this PoT already
// links out to real canada.ca pages elsewhere (see mfe-pot-msca-shell's own
// GC header/footer). Opened in a new tab (target + rel below) since it
// leaves the MSCA session entirely.
const CRA_MY_ACCOUNT_URL =
  'https://www.canada.ca/en/revenue-agency/services/e-services/cra-login-services/my-account-cra-individuals.html';

// Every link below targets a route the shell (mfe-pot-msca-shell) already
// serves for real -- none of these are placeholders. A plain <a>/scds-link
// href is correct here, not a client-side route: dashboard-mfe is a
// federated remote with no router of its own, so a link to another route
// crosses through a full top-level navigation, the same convention every
// other cross-remote link in this family uses (see
// mfe-pot-platform/CLAUDE.md's "Navigation inside a remote" section).
const LIFE_EVENTS_PATH = '/life-events';
const JOB_BANK_PATH = '/job-bank';
const PROFILE_PATH = '/profile';

const CARD_STYLE: React.CSSProperties = {
  background: 'var(--scds-color-white)',
  border: '1px solid var(--scds-border-color)',
  borderRadius: 'var(--scds-radius-md)',
  padding: 'var(--scds-space-5)',
  boxSizing: 'border-box',
};

// scds-icon itself is a bare stroked SVG with no background (see
// shared-ui-scds-core's scds-icon.tsx/scds-card.css) -- the light-blue
// square behind every icon in the Figma reference is this screen's own
// styling, not something the component provides. brand-50/brand-500 are
// real tokens (shared-ui-scds-core's scds.css), not hardcoded colors.
const ICON_BADGE_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '2.5rem',
  height: '2.5rem',
  borderRadius: 'var(--scds-radius-sm)',
  background: 'var(--scds-color-brand-50)',
  color: 'var(--scds-color-brand-500)',
  flexShrink: 0,
};

export interface NewUserScreenProps {
  t: (key: string, params?: Record<string, string | number>) => string;
}

/**
 * Shown instead of the current dashboard + its page chrome when
 * epayroll-ube's live employment signal says this citizen has never had a
 * job separation on record (spec 017-dashboard-new-user-state.md -- see
 * App.tsx's own DashboardGate for the switch). Deliberately does not reuse
 * App.tsx's `chrome.heading`/Overview's greeting `h2` -- this screen owns
 * its own, single h1 instead, since it replaces the chrome entirely rather
 * than sitting underneath it.
 *
 * Layout matches the Figma reference for this state (Get started / Your
 * benefits + Recent payments / Quick actions / Access other government
 * services) rather than being invented from the acceptance criteria alone
 * -- see this file's own git history for the earlier, criteria-only draft.
 * "Your benefits" and "Recent payments" stay static empty states, not
 * backed by dashboard-bff -- a citizen with no employment history on record
 * has no benefits or payments to fetch, so calling the BFF here would only
 * ever return the same empty result at the cost of a real request every
 * sign-in.
 *
 * Copy here (English and the French draft in assets/i18n/fr.json) is still
 * provisional -- transcribed from the Figma reference by hand, not pulled
 * from it programmatically, and the French is an unreviewed translation --
 * flagged for review once docs/specs/017-dashboard-new-user-state.md
 * actually exists.
 */
export function NewUserScreen({ t }: NewUserScreenProps) {
  const [citizenName, setCitizenName] = useState(() => getStoredSession()?.name ?? '');

  useEffect(() => onSessionChange((session) => setCitizenName(session?.name ?? '')), []);

  const firstName = citizenName.trim().split(/\s+/)[0] ?? '';

  return (
    <section
      className="dashboard-new-user"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-6)', paddingBlock: 'var(--scds-space-5)' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-3)' }}>
        <scds-heading tag="h1">
          {firstName ? t('newUser.greetingWithName', { name: firstName }) : t('newUser.greeting')}
        </scds-heading>
        <p>{t('newUser.subtitle')}</p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-3)' }}>
        <scds-heading tag="h2">{t('newUser.getStarted.heading')}</scds-heading>
        <scds-card
          card-title={t('newUser.getStarted.title')}
          card-title-tag="h3"
          description={t('newUser.getStarted.description')}
          layout="horizontal"
        >
          <span slot="scdsCardIcon" style={ICON_BADGE_STYLE}>
            <scds-icon name="document"></scds-icon>
          </span>
          <scds-link slot="scdsCardActions" href={LIFE_EVENTS_PATH} icon-name="arrow-right">
            {t('newUser.getStarted.cta')}
          </scds-link>
        </scds-card>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(22rem, 1fr))',
          gap: 'var(--scds-space-5)',
          alignItems: 'start',
        }}
      >
        <section className="dashboard-new-user__benefits" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-3)' }}>
          <scds-heading tag="h2">{t('newUser.benefits.heading')}</scds-heading>
          <div style={{ ...CARD_STYLE, display: 'flex', gap: 'var(--scds-space-3)' }}>
            <span style={ICON_BADGE_STYLE}>
              <scds-icon name="document"></scds-icon>
            </span>
            <div>
              <scds-heading tag="h3">{t('newUser.benefits.emptyTitle')}</scds-heading>
              <p>
                {t('newUser.benefits.emptyBody')}{' '}
                <scds-link href={LIFE_EVENTS_PATH}>{t('newUser.benefits.applyLink')}</scds-link>
                {t('newUser.benefits.applySuffix')}
              </p>
            </div>
          </div>
        </section>

        <section className="dashboard-new-user__payments" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-3)' }}>
          <scds-heading tag="h2">{t('newUser.payments.heading')}</scds-heading>
          <div style={{ ...CARD_STYLE, display: 'flex', gap: 'var(--scds-space-3)' }}>
            <span style={ICON_BADGE_STYLE}>
              <scds-icon name="finance"></scds-icon>
            </span>
            <div>
              <scds-heading tag="h3">{t('newUser.payments.emptyTitle')}</scds-heading>
              <p>{t('newUser.payments.emptyBody')}</p>
            </div>
          </div>
        </section>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-3)' }}>
        <scds-heading tag="h2">{t('newUser.quickActions.heading')}</scds-heading>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))',
            gap: 'var(--scds-space-5)',
            alignItems: 'start',
          }}
        >
          <scds-card
            card-title={t('newUser.quickActions.jobs.title')}
            card-title-tag="h3"
            description={t('newUser.quickActions.jobs.description')}
            layout="horizontal"
          >
            <span slot="scdsCardIcon" style={ICON_BADGE_STYLE}>
              <scds-icon name="briefcase"></scds-icon>
            </span>
            <scds-link slot="scdsCardActions" href={JOB_BANK_PATH} icon-name="arrow-right">
              {t('newUser.quickActions.jobs.cta')}
            </scds-link>
          </scds-card>

          <scds-card
            card-title={t('newUser.quickActions.apply.title')}
            card-title-tag="h3"
            description={t('newUser.quickActions.apply.description')}
            layout="horizontal"
          >
            <span slot="scdsCardIcon" style={ICON_BADGE_STYLE}>
              <scds-icon name="document"></scds-icon>
            </span>
            <scds-link slot="scdsCardActions" href={LIFE_EVENTS_PATH} icon-name="arrow-right">
              {t('newUser.quickActions.apply.cta')}
            </scds-link>
          </scds-card>

          <scds-card
            card-title={t('newUser.quickActions.profile.title')}
            card-title-tag="h3"
            description={t('newUser.quickActions.profile.description')}
            layout="horizontal"
          >
            <span slot="scdsCardIcon" style={ICON_BADGE_STYLE}>
              <scds-icon name="user"></scds-icon>
            </span>
            <scds-link slot="scdsCardActions" href={PROFILE_PATH} icon-name="arrow-right">
              {t('newUser.quickActions.profile.cta')}
            </scds-link>
          </scds-card>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--scds-space-3)' }}>
        <scds-heading tag="h2">{t('newUser.otherServices.heading')}</scds-heading>
        <scds-card
          card-title={t('newUser.craCard.title')}
          card-title-tag="h3"
          description={t('newUser.craCard.description')}
          href={CRA_MY_ACCOUNT_URL}
          target="_blank"
          rel="noopener noreferrer"
          layout="horizontal"
        >
          <span slot="scdsCardIcon" style={ICON_BADGE_STYLE}>
            <scds-icon name="finance"></scds-icon>
          </span>
        </scds-card>
      </div>
    </section>
  );
}
